/**
 * RelatorioGeraisFilters - Shared filter bar for all general reports
 */
import React from 'react';
import { Filter, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { MultiSelectFilter } from '../MultiSelectFilter';
import type { FilterOption } from '../MultiSelectFilter';

interface Props {
  dataInicio: string;
  setDataInicio: (v: string) => void;
  dataFim: string;
  setDataFim: (v: string) => void;
  selectedHospitals: number[];
  setSelectedHospitals: (v: number[]) => void;
  selectedClientes: number[];
  setSelectedClientes: (v: number[]) => void;
  selectedAuditores: number[];
  setSelectedAuditores: (v: number[]) => void;
  hospitalOptions: FilterOption[];
  clienteOptions: FilterOption[];
  auditorOptions: FilterOption[];
  allFiltersExpanded: boolean;
  setAllFiltersExpanded: (v: boolean) => void;
  activeFilterCount: number;
  clearAllFilters: () => void;
  // Extra filters depending on selected report
  selectedReport: string | null;
  // Status filter (agendamentos)
  selectedStatus?: string[];
  setSelectedStatus?: (v: string[]) => void;
  // Faixa aging filter (consolidado detalhado)
  selectedFaixaAging?: string[];
  setSelectedFaixaAging?: (v: string[]) => void;
}

const STATUS_OPTIONS: FilterOption[] = [
  { value: 'PRE_AGENDADA', label: 'Pré-Agendada' },
  { value: 'AGENDADA', label: 'Agendada' },
  { value: 'REAGENDADA', label: 'Reagendada' },
  { value: 'CONFIRMADA', label: 'Confirmada' },
  { value: 'EM_EXECUCAO', label: 'Em Execução' },
  { value: 'EXECUTADA', label: 'Executada' },
  { value: 'NAO_EXECUTADA', label: 'Não Executada' },
  { value: 'CANCELADA', label: 'Cancelada' },
];

const FAIXA_AGING_OPTIONS: FilterOption[] = [
  { value: '1', label: 'Expirado' },
  { value: '2', label: '0 a 30 dias' },
  { value: '3', label: '31 a 60 dias' },
  { value: '4', label: '61 a 90 dias' },
  { value: '5', label: '91 a 180 dias' },
  { value: '6', label: '> 180 dias' },
];

export const RelatorioGeraisFilters: React.FC<Props> = ({
  dataInicio,
  setDataInicio,
  dataFim,
  setDataFim,
  selectedHospitals,
  setSelectedHospitals,
  selectedClientes,
  setSelectedClientes,
  selectedAuditores,
  setSelectedAuditores,
  hospitalOptions,
  clienteOptions,
  auditorOptions,
  allFiltersExpanded,
  setAllFiltersExpanded,
  activeFilterCount,
  clearAllFilters,
  selectedReport,
  selectedStatus,
  setSelectedStatus,
  selectedFaixaAging,
  setSelectedFaixaAging,
}) => {
  const showStatusFilter = selectedReport === 'agendamentos';
  const showAgingFilter = selectedReport === 'consolidado-detalhado' || selectedReport === 'segregacao-consolidado';

  return (
    <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filtros</span>
          {activeFilterCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
              {activeFilterCount} ativos
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAllFiltersExpanded(!allFiltersExpanded)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            {allFiltersExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {allFiltersExpanded ? 'Recolher' : 'Expandir'}
          </button>
          <button
            onClick={clearAllFilters}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-colors"
          >
            <Trash2 className="w-3 h-3" />
            Limpar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3">
        {/* Date range */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Data Início</label>
          <input
            type="date"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Data Fim</label>
          <input
            type="date"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Hospital */}
        <MultiSelectFilter
          label="Hospital"
          options={hospitalOptions}
          selectedValues={selectedHospitals}
          onChange={(vals) => setSelectedHospitals(vals as number[])}
          expanded={allFiltersExpanded}
          placeholder="Todos"
        />

        {/* Cliente */}
        <MultiSelectFilter
          label="Cliente"
          options={clienteOptions}
          selectedValues={selectedClientes}
          onChange={(vals) => setSelectedClientes(vals as number[])}
          expanded={allFiltersExpanded}
          placeholder="Todos"
        />

        {/* Auditor */}
        <MultiSelectFilter
          label="Auditor"
          options={auditorOptions}
          selectedValues={selectedAuditores}
          onChange={(vals) => setSelectedAuditores(vals as number[])}
          expanded={allFiltersExpanded}
          placeholder="Todos"
        />

        {/* Status (only for agendamentos) */}
        {showStatusFilter && setSelectedStatus && (
          <MultiSelectFilter
            label="Status"
            options={STATUS_OPTIONS}
            selectedValues={selectedStatus || []}
            onChange={(vals) => setSelectedStatus(vals as string[])}
            expanded={allFiltersExpanded}
            placeholder="Todos"
          />
        )}

        {/* Faixa Aging (only for consolidado detalhado) */}
        {showAgingFilter && setSelectedFaixaAging && (
          <MultiSelectFilter
            label="Faixa Aging"
            options={FAIXA_AGING_OPTIONS}
            selectedValues={selectedFaixaAging || []}
            onChange={(vals) => setSelectedFaixaAging(vals as string[])}
            expanded={allFiltersExpanded}
            placeholder="Todas"
          />
        )}
      </div>
    </div>
  );
};
